import { useEffect, useState } from "react";
import {
    fetchBusinessCaseDeadline,
    fetchPhase,
    runTranslationsBackfill,
    setPhaseSchedule,
    type BusinessCaseWindow,
    type PhaseState,
} from "../api/tcg";
import AdminViews from "./adminViews.component";
import AdminActions from "./adminActions.component";
import BusinessCaseDeadline from "./businessCaseDeadline.component";
import AdminExport from "./adminExport.component";
import AdminCategories from "./adminCategories.component";
import AdminBusinessCases from "./adminBusinessCases.component";
import AdminContent from "./adminContent.component";
import AdminLayout from "./adminLayout.component";
import AdminUsers from "./adminUsers.component";
import AdminResults from "./adminResults.component";
import AdminNewsCards from "./adminNewsCards.component";

type Section =
    | "overview" | "phases" | "nominations" | "voting"
    | "businesscases" | "export" | "content" | "categories" | "admins";

const SECTIONS: { key: Section; label: string }[] = [
    { key: "overview", label: "Übersicht" },
    { key: "phases", label: "Phasen & Termine" },
    { key: "nominations", label: "Nominierungen" },
    { key: "voting", label: "Voting" },
    { key: "businesscases", label: "Business Cases" },
    { key: "export", label: "Export" },
    { key: "content", label: "Inhalte" },
    { key: "categories", label: "Kategorien" },
    { key: "admins", label: "Admins" },
];

function formatDate(iso: string | null): string {
    if (!iso) return "—";
    return new Date(iso).toLocaleString();
}

function toLocalInput(iso: string | null): string {
    if (!iso) return "";
    const d = new Date(iso);
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function AdminDashboard({ token, isOwner, onLogout }: { token: string; isOwner: boolean; onLogout: () => void }) {
    const [section, setSection] = useState<Section>("overview");
    const [phase, setPhase] = useState<PhaseState | null>(null);
    const [businessCaseWindow, setBusinessCaseWindowState] = useState<BusinessCaseWindow | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [message, setMessage] = useState<string | null>(null);
    const [busy, setBusy] = useState(false);
    const [translationBusy, setTranslationBusy] = useState(false);
    const [translationMessage, setTranslationMessage] = useState<string | null>(null);
    const [translationError, setTranslationError] = useState<string | null>(null);
    const [nomStart, setNomStart] = useState("");
    const [nomEnd, setNomEnd] = useState("");
    const [voteStart, setVoteStart] = useState("");
    const [voteEnd, setVoteEnd] = useState("");

    async function refresh() {
        try {
            const p = await fetchPhase();
            setPhase(p);
            setNomStart(toLocalInput(p.nomination_starts_at));
            setNomEnd(toLocalInput(p.nomination_ends_at));
            setVoteStart(toLocalInput(p.voting_starts_at));
            setVoteEnd(toLocalInput(p.voting_ends_at));
        } catch (err) {
            setError(err instanceof Error ? err.message : "Konnte Phase nicht laden.");
        }
    }

    async function refreshBusinessCaseWindow() {
        try {
            setBusinessCaseWindowState(await fetchBusinessCaseDeadline());
        } catch {
            setBusinessCaseWindowState(null);
        }
    }

    useEffect(() => {
        refresh();
        refreshBusinessCaseWindow();
    }, []);

    async function handleSave() {
        setBusy(true);
        setError(null);
        setMessage(null);

        try {
            const updated = await setPhaseSchedule(token, {
                nomination_starts_at: nomStart || null,
                nomination_ends_at: nomEnd || null,
                voting_starts_at: voteStart || null,
                voting_ends_at: voteEnd || null,
            });
            setPhase(updated);
            setMessage("Zeitplan gespeichert.");
        } catch (err) {
            setError(err instanceof Error ? err.message : "Speichern fehlgeschlagen.");
        } finally {
            setBusy(false);
        }
    }

    async function handleTranslationBackfill() {
        const confirmed = window.confirm(
            "Bestehende Startseiten-Texte und Kategorien jetzt mit DeepL übersetzen?\n\nDas kann je nach Datenmenge etwas dauern."
        );

        if (!confirmed) return;

        setTranslationBusy(true);
        setTranslationMessage(null);
        setTranslationError(null);

        try {
            const result = await runTranslationsBackfill(token);
            setTranslationMessage(
                `${result.message} Inhalte: ${result.content}, Kategorien: ${result.categories}.`
            );
        } catch (err) {
            setTranslationError(err instanceof Error ? err.message : "Übersetzung konnte nicht gestartet werden.");
        } finally {
            setTranslationBusy(false);
        }
    }

    const phaseLabel = phase
        ? (phase.phase === "IDLE" ? "Ruhezustand"
            : phase.phase === "NOMINATION" ? "Nominierung läuft" : "Voting läuft")
        : "…";

    let statusSummary = "";
    if (phase) {
        if (phase.phase === "NOMINATION") statusSummary = `Endet ${formatDate(phase.nomination_ends_at)}`;
        else if (phase.phase === "VOTING") statusSummary = `Endet ${formatDate(phase.voting_ends_at)}`;
        else statusSummary = "Aktuell keine aktive Phase";
    }

    const businessCaseSummary =
        businessCaseWindow?.starts_at || businessCaseWindow?.deadline
            ? `Business Case: ${formatDate(businessCaseWindow.starts_at)} → ${formatDate(businessCaseWindow.deadline)}`
            : "Business Case: kein Zeitplan";

    return (
        <div className="admin-container admin-shell">
            <div className="admin-header">
                <h1>Admin Dashboard</h1>
                <button className="admin-btn-secondary" onClick={onLogout}>Logout</button>
            </div>

            <div className="admin-statusbar">
                {phase && (
                    <span className={`admin-phase-badge phase-${phase.phase.toLowerCase()}`}>{phaseLabel}</span>
                )}
                <span className="admin-statusbar-text">{statusSummary}</span>
                <span className="admin-statusbar-divider" aria-hidden="true"></span>
                <span className="admin-statusbar-text admin-statusbar-business">{businessCaseSummary}</span>
            </div>

            {error && <p className="admin-error">{error}</p>}

            <div className="admin-body">
                <nav className="admin-sidebar">
                    {SECTIONS.map((s) => (
                        <button
                            key={s.key}
                            className={`admin-sidebar-item ${section === s.key ? "active" : ""}`}
                            onClick={() => setSection(s.key)}
                        >
                            {s.label}
                        </button>
                    ))}
                </nav>

                <main className={`admin-content admin-content-${section}`}>
                    {section === "overview" && (
                        <section className="admin-card admin-status-card">
                            <div className="admin-status-head">
                                <h2 className="admin-card-title">Aktuelle Phase</h2>
                                {phase && (
                                    <span className={`admin-phase-badge phase-${phase.phase.toLowerCase()}`}>{phaseLabel}</span>
                                )}
                            </div>
                            {!phase ? (
                                <p className="admin-note">Lädt…</p>
                            ) : (
                                <div className="admin-phase-info">
                                    <p>
                                        <span className="admin-phase-info-label">Nominierung</span>
                                        {formatDate(phase.nomination_starts_at)} → {formatDate(phase.nomination_ends_at)}
                                    </p>
                                    <p>
                                        <span className="admin-phase-info-label">Voting</span>
                                        {formatDate(phase.voting_starts_at)} → {formatDate(phase.voting_ends_at)}
                                    </p>
                                </div>
                            )}
                        </section>
                    )}

                    {section === "phases" && (
                        <>
                            <section className="admin-card">
                                <h2 className="admin-card-title">Phasen-Zeitplan</h2>
                                <p className="admin-note" style={{ marginBottom: "18px" }}>
                                    Trage Start und Ende ein. Das System schaltet automatisch zur richtigen Zeit.
                                    Nominierung und Voting dürfen sich nicht überschneiden - plane eine Lücke dazwischen.
                                </p>
                                {message && <p className="admin-success">{message}</p>}
                                <div className="admin-phase-grid">
                                    <div className="admin-control-row">
                                        <label className="admin-label">Nominierung - Start</label>
                                        <input className="admin-input" type="datetime-local" value={nomStart} onChange={(e) => setNomStart(e.target.value)} />
                                        <label className="admin-label">Nominierung - Ende</label>
                                        <input className="admin-input" type="datetime-local" value={nomEnd} onChange={(e) => setNomEnd(e.target.value)} />
                                    </div>
                                    <div className="admin-control-row">
                                        <label className="admin-label">Voting - Start</label>
                                        <input className="admin-input" type="datetime-local" value={voteStart} onChange={(e) => setVoteStart(e.target.value)} />
                                        <label className="admin-label">Voting - Ende</label>
                                        <input className="admin-input" type="datetime-local" value={voteEnd} onChange={(e) => setVoteEnd(e.target.value)} />
                                    </div>
                                </div>
                                <div style={{ marginTop: "20px" }}>
                                    <button className="admin-btn-primary" onClick={handleSave} disabled={busy}>Speichern</button>
                                </div>
                            </section>
                            <BusinessCaseDeadline token={token} onUpdated={refreshBusinessCaseWindow} />
                        </>
                    )}

                    {section === "nominations" && (
                        <AdminViews token={token} fixedView="standings" />
                    )}

                    {section === "voting" && (
                        <>
                            <AdminViews token={token} fixedView="results" />
                            <AdminActions token={token} />
                            <AdminResults token={token} />
                        </>
                    )}

                    {section === "businesscases" && <AdminBusinessCases token={token} />}
                    {section === "export" && <AdminExport token={token} />}

                    {section === "content" && (
                        <>
                            <section className="admin-card">
                                <h2 className="admin-card-title">Übersetzungen</h2>
                                <p className="admin-note" style={{ marginBottom: "18px" }}>
                                    Übersetzt bestehende Startseiten-Texte und Kategorien nachträglich mit DeepL.
                                    Neue oder geänderte Inhalte werden beim Speichern automatisch übersetzt.
                                </p>

                                {translationMessage && <p className="admin-success">{translationMessage}</p>}
                                {translationError && <p className="admin-error">{translationError}</p>}

                                <button
                                    className="admin-btn-primary"
                                    onClick={handleTranslationBackfill}
                                    disabled={translationBusy}
                                >
                                    {translationBusy ? "Übersetzt…" : "Inhalte übersetzen"}
                                </button>
                            </section>

                            <AdminLayout token={token} />
                            <AdminContent token={token} />
                            <AdminNewsCards token={token} />
                        </>
                    )}

                    {section === "categories" && <AdminCategories token={token} />}
                    {section === "admins" && <AdminUsers token={token} isOwner={isOwner} />}
                </main>
            </div>
        </div>
    );
}

export default AdminDashboard;
